import {skipToken, useQuery} from '@tanstack/react-query';
import type {RoadmapAuditFindingsResponse} from 'client/models/RoadmapAuditFindingsResponse';
import {useEffect, useMemo, useState} from 'react';
import {fetchRoadmapAuditFindings, type RoadmapAuditFindingsFilters} from 'views/roadmap/api';
import type {RoadmapAuditFilters} from 'views/roadmap/queryParams';

const DEFAULT_PAGE_LIMIT = 50;

export interface RoadmapAuditFindingsSelection extends RoadmapAuditFilters {
  timeframe: string | null;
  quarter: string | null;
  scopeKey: string | null;
  groupIds?: string[];
  statuses?: string[];
  assigneeScopeEmployeeId?: string | null;
}

const stableValues = (values: string[] | null | undefined) => [...(values ?? [])].sort();

export const roadmapAuditFindingsQueryKeys = {
  all: ['roadmap', 'audit-findings'] as const,
  page: (filters: RoadmapAuditFindingsFilters) =>
    [
      ...roadmapAuditFindingsQueryKeys.all,
      {
        timeframe: filters.timeframe,
        quarter: filters.quarter,
        scopeKey: filters.scopeKey ?? null,
        groupIds: stableValues(filters.groupIds),
        assignee: filters.assignee ?? 'all',
        assigneeKeys: stableValues(filters.assigneeKeys),
        assigneeScopeEmployeeId: filters.assigneeScopeEmployeeId ?? null,
        ruleIds: stableValues(filters.ruleIds),
        ruleMode: filters.ruleMode ?? 'any',
        statuses: stableValues(filters.statuses),
        query: filters.query ?? null,
        cursor: filters.cursor ?? null,
        limit: filters.limit ?? DEFAULT_PAGE_LIMIT,
      },
    ] as const,
};

export const getRoadmapAuditFindingsFilterKey = (selection: RoadmapAuditFindingsSelection) =>
  JSON.stringify({
    timeframe: selection.timeframe,
    quarter: selection.quarter,
    scopeKey: selection.scopeKey,
    groupIds: stableValues(selection.groupIds),
    assignee: selection.assignee,
    assigneeKeys: stableValues(selection.assigneeKeys),
    assigneeScopeEmployeeId: selection.assigneeScopeEmployeeId ?? null,
    ruleIds: stableValues(selection.ruleIds),
    ruleMode: selection.ruleMode,
    statuses: stableValues(selection.statuses),
    query: selection.query,
  });

export const useRoadmapAuditPagination = (filterKey: string) => {
  const [pagination, setPagination] = useState<{
    filterKey: string;
    cursors: Array<string | null>;
    pageIndex: number;
  }>({filterKey, cursors: [null], pageIndex: 0});

  const current =
    pagination.filterKey === filterKey ? pagination : {filterKey, cursors: [null], pageIndex: 0};

  useEffect(() => {
    setPagination((previous) =>
      previous.filterKey === filterKey ? previous : {filterKey, cursors: [null], pageIndex: 0}
    );
  }, [filterKey]);

  return {
    cursor: current.cursors[current.pageIndex] ?? null,
    pageIndex: current.pageIndex,
    hasPreviousPage: current.pageIndex > 0,
    nextPage: (cursor: string) =>
      setPagination((previous) => {
        const base =
          previous.filterKey === filterKey ? previous : {filterKey, cursors: [null], pageIndex: 0};
        return {
          ...base,
          cursors: [...base.cursors.slice(0, base.pageIndex + 1), cursor],
          pageIndex: base.pageIndex + 1,
        };
      }),
    previousPage: () =>
      setPagination((previous) => {
        const base =
          previous.filterKey === filterKey ? previous : {filterKey, cursors: [null], pageIndex: 0};
        return {...base, pageIndex: Math.max(0, base.pageIndex - 1)};
      }),
    resetPagination: () => setPagination({filterKey, cursors: [null], pageIndex: 0}),
  };
};

export const useRoadmapAuditFindings = ({
  enabled,
  selection,
  limit = DEFAULT_PAGE_LIMIT,
}: {
  enabled: boolean;
  selection: RoadmapAuditFindingsSelection;
  limit?: number;
}) => {
  const filterKey = useMemo(() => getRoadmapAuditFindingsFilterKey(selection), [selection]);
  const pagination = useRoadmapAuditPagination(filterKey);
  const canQuery = Boolean(enabled && selection.timeframe && selection.quarter);

  const request: RoadmapAuditFindingsFilters | null =
    selection.timeframe && selection.quarter
      ? {
          timeframe: selection.timeframe,
          quarter: selection.quarter,
          scopeKey: selection.scopeKey,
          groupIds: selection.groupIds,
          assignee: selection.assignee,
          assigneeKeys: selection.assigneeKeys,
          assigneeScopeEmployeeId: selection.assigneeScopeEmployeeId,
          ruleIds: selection.ruleIds,
          ruleMode: selection.ruleMode,
          statuses: selection.statuses,
          query: selection.query,
          cursor: pagination.cursor,
          limit,
        }
      : null;

  const query = useQuery({
    queryKey: request
      ? roadmapAuditFindingsQueryKeys.page(request)
      : [...roadmapAuditFindingsQueryKeys.all, 'disabled'],
    enabled: canQuery,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    queryFn: request
      ? ({queryKey}) => fetchRoadmapAuditFindings(queryKey[2] as RoadmapAuditFindingsFilters)
      : skipToken,
  });

  const [loadedPages, setLoadedPages] = useState<{
    filterKey: string;
    pages: Array<RoadmapAuditFindingsResponse | undefined>;
  }>({filterKey, pages: []});

  useEffect(() => {
    setLoadedPages((previous) =>
      previous.filterKey === filterKey ? previous : {filterKey, pages: []}
    );
  }, [filterKey]);

  useEffect(() => {
    if (!query.data) return;
    setLoadedPages((previous) => {
      const currentPages = previous.filterKey === filterKey ? [...previous.pages] : [];
      currentPages[pagination.pageIndex] = query.data;
      return {filterKey, pages: currentPages};
    });
  }, [filterKey, pagination.pageIndex, query.data]);

  const accumulatedData = useMemo(() => {
    const pages = loadedPages.filterKey === filterKey ? loadedPages.pages : [];
    const currentPages = [...pages];
    if (query.data) {
      currentPages[pagination.pageIndex] = query.data;
    }
    const responses = currentPages.filter((page): page is RoadmapAuditFindingsResponse =>
      Boolean(page)
    );
    if (responses.length === 0) return undefined;

    const latest = responses[responses.length - 1];
    const seenInitiativeKeys = new Set<string>();
    const items = responses.flatMap((response) =>
      response.items.filter((item) => {
        if (seenInitiativeKeys.has(item.initiative.key)) return false;
        seenInitiativeKeys.add(item.initiative.key);
        return true;
      })
    );

    return {...latest, items};
  }, [filterKey, loadedPages, pagination.pageIndex, query.data]);

  return {
    ...query,
    data: accumulatedData,
    isEnabled: canQuery,
    filterKey,
    pageIndex: pagination.pageIndex,
    hasPreviousPage: pagination.hasPreviousPage,
    nextCursor: accumulatedData?.page.next_cursor ?? null,
    nextPage: pagination.nextPage,
    previousPage: pagination.previousPage,
    resetPagination: pagination.resetPagination,
  };
};
